import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, Badge, Button, Alert, Modal } from '../components/design-system';
import { Input, TextArea, Select } from '../components/design-system';
import {
  MapPin, Clock, Users, Package, Box, ChevronRight,
  DollarSign,
} from 'lucide-react';
import courierApi from '../services/courierApi';

export const MarketplacePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [hasProviderProfile, setHasProviderProfile] = useState(false);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { role: 'provider' };
      if (filter !== 'all') params.type = filter;
      const res = await courierApi.listRequests(params);
      setRequests(res.requests || []);

      try {
        await courierApi.getMyProviderProfile();
        setHasProviderProfile(true);
      } catch {
        setHasProviderProfile(false);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const typeIcons = { persons: Users, parcel: Package, large_objects: Box };
  const urgencyColors = { asap: 'danger', normal: 'default', flexible: 'info' };

  return (
    <div className="p-4 pb-2">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold text-gray-900">{t('provider.marketplace')}</h1>
      </div>

      {!hasProviderProfile && (
        <Alert variant="warning" className="mb-4">
          {t('provider.noProfile')}
        </Alert>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
        {[
          { value: 'all', label: t('common.appName') },
          { value: 'persons', label: t('request.types.persons') },
          { value: 'parcel', label: t('request.types.parcel') },
          { value: 'large_objects', label: t('request.types.large_objects') },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              filter === f.value
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.value === 'all' ? 'All' : f.label}
          </button>
        ))}
      </div>

      {error && (
        <Alert variant="error" className="mb-4" dismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-3 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">{t('provider.noMatchingRequests')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const TypeIcon = typeIcons[req.type] || Package;
            return (
              <Card key={req.id} padding="sm" className="hover:border-primary-300 transition-colors">
                <button
                  onClick={() => {
                    setSelectedRequest(req);
                    setShowOfferModal(true);
                  }}
                  className="w-full text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <TypeIcon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{req.title}</p>
                        <Badge variant={urgencyColors[req.urgency]} size="sm">
                          {t(`request.urgencyOptions.${req.urgency}`)}
                        </Badge>
                      </div>
                      {req.description && (
                        <p className="text-xs text-gray-600 line-clamp-2 mb-2">{req.description}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {req.pickupAddress || 'Pickup'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {req.scheduledTime
                            ? new Date(req.scheduledTime).toLocaleDateString()
                            : 'ASAP'}
                        </span>
                      </div>
                      {(req.budgetMin || req.budgetMax) && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <DollarSign className="h-3 w-3 text-green-500" />
                          <span className="text-xs font-medium text-green-600">
                            {t('request.budgetLabel')}: {req.budgetMin && req.budgetMax
                              ? `${req.budgetMin}–${req.budgetMax} ${req.budgetCurrency || 'EUR'}`
                              : req.budgetMax
                                ? `${t('request.budgetUpTo')} ${req.budgetMax} ${req.budgetCurrency || 'EUR'}`
                                : `${t('request.budgetFrom')} ${req.budgetMin} ${req.budgetCurrency || 'EUR'}`
                            }
                          </span>
                        </div>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  </div>
                </button>
              </Card>
            );
          })}
        </div>
      )}

      {/* Make Offer Modal */}
      {selectedRequest && (
        <MakeOfferModal
          isOpen={showOfferModal}
          onClose={() => {
            setShowOfferModal(false);
            setSelectedRequest(null);
          }}
          request={selectedRequest}
          onSubmitted={() => {
            setShowOfferModal(false);
            setSelectedRequest(null);
            loadData();
          }}
          hasProfile={hasProviderProfile}
        />
      )}
    </div>
  );
};

const MakeOfferModal = ({ isOpen, onClose, request, onSubmitted, hasProfile }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    feeAmount: '',
    feeCurrency: 'EUR',
    estimatedPickupTime: '',
    estimatedDeliveryTime: '',
    paymentMethods: ['cash'],
    termsText: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasProfile) {
      setError('You need a provider profile to make offers');
      return;
    }
    setError('');
    setLoading(true);

    try {
      await courierApi.createOffer({
        requestId: request.id,
        feeAmount: parseFloat(form.feeAmount),
        feeCurrency: form.feeCurrency,
        estimatedPickupTime: form.estimatedPickupTime || null,
        estimatedDeliveryTime: form.estimatedDeliveryTime || null,
        paymentMethods: form.paymentMethods,
        termsText: form.termsText || null,
      });
      onSubmitted();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePayment = (method) => {
    setForm(f => ({
      ...f,
      paymentMethods: f.paymentMethods.includes(method)
        ? f.paymentMethods.filter(m => m !== method)
        : [...f.paymentMethods, method],
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('offer.makeOffer')} size="md">
      {/* Request summary */}
      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <p className="text-sm font-semibold text-gray-900">{request.title}</p>
        <p className="text-xs text-gray-600 mt-1">
          {t(`request.types.${request.type}`)} &bull; {t(`request.urgencyOptions.${request.urgency}`)}
        </p>
        <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
          <MapPin className="h-3 w-3" />
          {request.pickupAddress} → {request.dropoffAddress}
        </div>
        {(request.budgetMin || request.budgetMax) && (
          <div className="flex items-center gap-1 mt-1.5 text-xs font-medium text-green-600">
            <DollarSign className="h-3 w-3" />
            {t('request.customerBudget')}: {request.budgetMin && request.budgetMax
              ? `${request.budgetMin}–${request.budgetMax} ${request.budgetCurrency || 'EUR'}`
              : request.budgetMax
                ? `${t('request.budgetUpTo')} ${request.budgetMax} ${request.budgetCurrency || 'EUR'}`
                : `${t('request.budgetFrom')} ${request.budgetMin} ${request.budgetCurrency || 'EUR'}`
            }
          </div>
        )}
      </div>

      {error && (
        <Alert variant="error" className="mb-4" dismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t('offer.fee')}
            type="number"
            min="0"
            step="0.01"
            placeholder={t('offer.feePlaceholder')}
            value={form.feeAmount}
            onChange={(e) => setForm({ ...form, feeAmount: e.target.value })}
            leftIcon={<DollarSign className="h-4 w-4 text-gray-400" />}
            required
            fullWidth
          />
          <Select
            label={t('offer.currency')}
            value={form.feeCurrency}
            onChange={(e) => setForm({ ...form, feeCurrency: e.target.value })}
            options={[
              { value: 'EUR', label: 'EUR' },
              { value: 'USD', label: 'USD' },
              { value: 'GBP', label: 'GBP' },
              { value: 'SEK', label: 'SEK' },
            ]}
            fullWidth
          />
        </div>

        <Input
          label={t('offer.estimatedPickup')}
          type="datetime-local"
          value={form.estimatedPickupTime}
          onChange={(e) => setForm({ ...form, estimatedPickupTime: e.target.value })}
          fullWidth
        />

        <Input
          label={t('offer.estimatedDelivery')}
          type="datetime-local"
          value={form.estimatedDeliveryTime}
          onChange={(e) => setForm({ ...form, estimatedDeliveryTime: e.target.value })}
          fullWidth
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('offer.paymentMethods')}
          </label>
          <div className="flex flex-wrap gap-2">
            {['cash', 'mobilepay', 'revolut', 'bank_transfer'].map((method) => (
              <button
                key={method}
                type="button"
                onClick={() => togglePayment(method)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  form.paymentMethods.includes(method)
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t(`offer.paymentOptions.${method}`)}
              </button>
            ))}
          </div>
        </div>

        <TextArea
          label={t('offer.terms')}
          placeholder={t('offer.termsPlaceholder')}
          value={form.termsText}
          onChange={(e) => setForm({ ...form, termsText: e.target.value })}
          rows={2}
          fullWidth
        />

        <Button type="submit" fullWidth loading={loading} size="lg">
          {t('offer.submitOffer')}
        </Button>
      </form>
    </Modal>
  );
};
